'use client';

import { useEffect, useRef, useState, type FormEvent } from 'react';
import { usePublicUi } from '@/lib/publicUi';
import {
  accountSettingsHref,
  accountSignInHref,
  resolveAccountProfileLinks,
  type AccountProfileLinks,
} from '@/lib/accountMenu';
import { InboxBell } from './InboxBell';
import styles from './AccountMenu.module.css';

type AuthUser = {
  uid: string;
  email?: string | null;
  displayName?: string | null;
};

type ContaindAuthSessionApi = {
  ensureAuth?: () => Promise<AuthUser | null>;
  onAuthChanged?: (fn: (user: AuthUser | null) => void) => () => void;
};

function authSession(): ContaindAuthSessionApi | undefined {
  if (typeof window === 'undefined') return undefined;
  return (window as Window & { ContaindAuthSession?: ContaindAuthSessionApi })
    .ContaindAuthSession;
}

function currentIdToken(): string | null {
  if (typeof window === 'undefined') return null;
  return (
    (window as Window & { __CONTAIND_ID_TOKEN__?: string | null })
      .__CONTAIND_ID_TOKEN__ ?? null
  );
}

function AccountIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
      <path d="M12 12a4.5 4.5 0 1 0 0-9 4.5 4.5 0 0 0 0 9Zm0 2.25c-4.14 0-7.5 2.11-7.5 4.72V21h15v-2.03c0-2.61-3.36-4.72-7.5-4.72Z" />
    </svg>
  );
}

async function signOutAndReload(): Promise<void> {
  try {
    const appUrl = 'https://www.gstatic.com/firebasejs/11.0.2/firebase-app.js';
    const authUrl = 'https://www.gstatic.com/firebasejs/11.0.2/firebase-auth.js';
    // CDN ESM — bundlers must not resolve these (same pattern as auth-session.js).
    const appMod = (await import(
      /* webpackIgnore: true */ /* @vite-ignore */ appUrl
    )) as { getApps: () => unknown[] };
    const authMod = (await import(
      /* webpackIgnore: true */ /* @vite-ignore */ authUrl
    )) as { getAuth: () => { signOut: () => Promise<void> } };
    if (!appMod.getApps().length) return;
    await authMod.getAuth().signOut();
    (window as Window & { __CONTAIND_ID_TOKEN__?: string | null }).__CONTAIND_ID_TOKEN__ =
      null;
  } catch {
    /* ignore */
  }
  location.reload();
}

export function AccountMenu() {
  const { locale, catalog } = usePublicUi();
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const [user, setUser] = useState<AuthUser | null>(null);
  const [profile, setProfile] = useState<AccountProfileLinks | null>(null);
  const [signInHref, setSignInHref] = useState(() =>
    accountSignInHref(locale, '/'),
  );

  useEffect(() => {
    setSignInHref(
      accountSignInHref(
        locale,
        typeof window !== 'undefined'
          ? window.location.href.split('#')[0] ?? window.location.href
          : '/',
      ),
    );
  }, [locale]);

  useEffect(() => {
    let cancelled = false;
    const session = authSession();

    const applyUser = (next: AuthUser | null) => {
      if (cancelled) return;
      setUser(next);
      if (!next) {
        setProfile(null);
        return;
      }
      const token = currentIdToken();
      if (!token) {
        setProfile({
          viewHref: accountSettingsHref(locale),
          editHref: accountSettingsHref(locale),
          label: 'Edit profile',
          avatarUrl: null,
        });
        return;
      }
      void resolveAccountProfileLinks(token, locale).then((links) => {
        if (!cancelled) setProfile(links);
      });
    };

    void (session?.ensureAuth?.() ?? Promise.resolve(null)).then(applyUser);

    const unsub = session?.onAuthChanged?.(applyUser);
    const onAuthChanged = (ev: Event) => {
      const detail = (ev as CustomEvent<{ user?: AuthUser | null }>).detail;
      applyUser(detail?.user ?? null);
    };
    const onAvatarChanged = (ev: Event) => {
      const url = (ev as CustomEvent<{ avatarUrl?: string | null }>).detail
        ?.avatarUrl;
      setProfile((prev) =>
        prev
          ? { ...prev, avatarUrl: url || null }
          : {
              viewHref: accountSettingsHref(locale),
              editHref: accountSettingsHref(locale),
              label: 'Edit profile',
              avatarUrl: url || null,
            },
      );
    };

    document.addEventListener('containd:auth-changed', onAuthChanged);
    document.addEventListener('containd:avatar-changed', onAvatarChanged);

    return () => {
      cancelled = true;
      unsub?.();
      document.removeEventListener('containd:auth-changed', onAuthChanged);
      document.removeEventListener('containd:avatar-changed', onAvatarChanged);
    };
  }, [locale]);

  useEffect(() => {
    const onPointerDown = (ev: PointerEvent) => {
      const el = detailsRef.current;
      if (!el?.open) return;
      if (ev.target instanceof Node && el.contains(ev.target)) return;
      el.open = false;
    };
    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      const el = detailsRef.current;
      if (el?.open) el.open = false;
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, []);

  const whoLabel =
    user?.email || user?.displayName || user?.uid || catalog.navAccount;
  const summaryLabel = user ? whoLabel : catalog.navAccount;
  const settingsHref = accountSettingsHref(locale);
  const profileHref = profile?.viewHref ?? settingsHref;
  const profileLinkText =
    profile?.label === 'View profile'
      ? catalog.navViewProfile
      : 'Edit profile';

  const onSignOut = (ev: FormEvent) => {
    ev.preventDefault();
    void signOutAndReload();
  };

  return (
    <>
      <InboxBell />
      <details
        ref={detailsRef}
        className={styles.menu}
        data-account-menu=""
        data-nav-menu="account"
      >
        <summary
          className={`${styles.summary}${profile?.avatarUrl ? ` ${styles.hasAvatar}` : ''}`}
          data-account-summary=""
          aria-label={summaryLabel}
          title={summaryLabel}
        >
          {profile?.avatarUrl ? (
            <img
              className={styles.avatar}
              src={profile.avatarUrl}
              alt=""
            />
          ) : (
            <AccountIcon />
          )}
        </summary>
        <div className={styles.panel}>
          {user ? (
            <div className={styles.who} data-account-who="">
              Signed in as{' '}
              <a href={profile?.editHref ?? settingsHref} data-account-who-link="">
                {whoLabel}
              </a>
            </div>
          ) : null}
          {!user ? (
            <a href={signInHref} data-account-signin="">
              {catalog.navSignIn}
            </a>
          ) : null}
          {user ? (
            <>
              <a href={profileHref} data-account-profile="">
                {profileLinkText}
              </a>
              <a href={settingsHref} data-account-settings="">
                {catalog.navSettings}
              </a>
              <button type="button" data-account-signout="" onClick={onSignOut}>
                {catalog.navSignOut}
              </button>
            </>
          ) : null}
        </div>
      </details>
    </>
  );
}
